import { useMemo, useState } from 'react';
import Ics204VersionStatusBar from 'components/IAP/planningView/widgets/AssignmentList/Ics204VersionStatusBar';
import Ics204VersionsDialog from 'components/IAP/planningView/widgets/AssignmentList/Ics204VersionsDialog';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useCreateDraftInternalLayerVersionMutation } from 'hooks/internalLayerVersion/useCreateDraftInternalLayerVersionMutation';
import { useListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import { useLayerSidebarPanelOptional } from './LayerSidebarPanel';
import { LayerPrivacyControl, layerPrivacyScope } from './LayerPrivacyControl';
import { useUscgMapContext } from './UscgMapContext';

function latestVersion(versions: InternalLayerVersion[]) {
  return versions.reduce<InternalLayerVersion | null>((best, version) => {
    if (!best || version.versionNumber > best.versionNumber) return version;
    return best;
  }, null);
}

export function useDraftNewInternalLayerVersion(layer: InternalLayer) {
  const panel = useLayerSidebarPanelOptional();
  const { setSelectedInternalLayerVersion, setSidebarSelectedInternalLayer } =
    useUscgMapContext();
  const createDraft = useCreateDraftInternalLayerVersionMutation();
  const { data } = useListInternalLayerVersionsQuery({
    filter: { internalLayerId: layer.id },
  });
  const versions = data?.data ?? [];
  const draftExists = versions.some((version) =>
    ['DRAFT', 'PENDING'].includes(version.approval?.approvalStatus || '')
  );
  const canCreate = layer.publishedAt != null || Boolean(layer.viewerCanEdit);

  const createNewDraft = async () => {
    const created = await createDraft.mutateAsync({
      internalLayerId: layer.id,
    });
    if (created.version) {
      setSelectedInternalLayerVersion(layer.id, created.version);
    }
    setSidebarSelectedInternalLayer(layer);
    panel?.setActivePanel('edit_layer_version');
  };

  return {
    createNewDraft,
    canCreate,
    draftExists,
    disabled: !canCreate || draftExists || createDraft.isPending,
  };
}

export function InternalLayerVersionBanner({
  layer,
}: {
  layer: InternalLayer;
}) {
  const { getSelectedVersionForLayer, setSelectedInternalLayerVersion } =
    useUscgMapContext();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const { data: roster = [] } = useGetRostersForGroup(layer.groupGuid);
  const { data } = useListInternalLayerVersionsQuery({
    filter: { internalLayerId: layer.id },
  });
  const versions = useMemo(() => data?.data ?? [], [data?.data]);
  const selected = getSelectedVersionForLayer(layer.id);
  const tip = latestVersion(versions);
  const shown = selected ?? tip;

  const nameByUserId = useMemo(() => {
    const names = new Map<string, string>();
    for (const person of roster) {
      if (!person.user_guid) continue;
      names.set(
        person.user_guid,
        person.user_name || person.email_address || person.user_guid
      );
    }
    return names;
  }, [roster]);

  const nameFor = (userId?: string | null) =>
    (userId && nameByUserId.get(userId)) || null;

  const updatedAt = shown?.updatedAt ?? layer.updatedAt;
  const updatedBy = shown?.updatedBy ?? layer.updatedBy;
  const viewingPast = viewingId != null && shown != null;
  const privacy = layerPrivacyScope(layer);
  const privacyControl = (
    <LayerPrivacyControl
      groupGuid={layer.groupGuid}
      incidentId={privacy.incidentId}
      scope={privacy.scope}
      layerId={layer.id}
    />
  );

  return (
    <>
      <Ics204VersionStatusBar
        embedded
        hideProgressRing
        hideSignedMenu
        signatures={[]}
        viewingPast={viewingPast}
        isCreatingSignedVersion={false}
        historyCount={versions.length}
        signedCount={0}
        createItems={[]}
        updatedAt={updatedAt}
        updatedByUserName={nameFor(updatedBy)}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenSigned={() => setHistoryOpen(true)}
        onViewLatest={() => {
          setViewingId(null);
          if (tip) setSelectedInternalLayerVersion(layer.id, tip);
        }}
        onCancelCreateSigned={() => {
          setViewingId(null);
          if (tip) setSelectedInternalLayerVersion(layer.id, tip);
        }}
        trailing={privacyControl}
      />
      <Ics204VersionsDialog
        open={historyOpen}
        kind="all"
        versions={versions.map((version) => ({
          id: version.id,
          updatedAt: version.updatedAt,
          updatedByUserName: nameFor(version.updatedBy),
        }))}
        tipId={tip?.id}
        description="View a previous version of this layer. Previous versions are read-only."
        statusLabel={(row) => {
          const version = versions.find((item) => item.id === row.id);
          const status = version?.approval?.approvalStatus ?? 'Draft';
          return `v${version?.versionNumber ?? ''} · ${status}`;
        }}
        onClose={() => setHistoryOpen(false)}
        onView={(row) => {
          const version = versions.find((item) => item.id === row.id);
          if (!version) return;
          setViewingId(tip && version.id === tip.id ? null : version.id);
          setSelectedInternalLayerVersion(layer.id, version);
          setHistoryOpen(false);
        }}
      />
    </>
  );
}

export function canPublishInternalLayer(
  layer: InternalLayer,
  userGuid?: string | null
) {
  return layer.publishedAt == null && layer.createdBy === userGuid;
}
